import { useCallback, useState } from 'react';

import type { RefCallback } from 'react';

/**
 * Reads the colour scheme in effect for an element, and keeps it up to date as the `data-color-scheme` attribute is
 * added to, removed from, or changed on any of the element's ancestors.
 *
 * The value is read from the inherited CSS `color-scheme` property that MUI emits alongside each colour scheme's
 * variables, so the cascade decides which ancestor wins.
 *
 * This hook is preferred over MUI's built-in `useColorScheme` hook because it allows us to scope the colour scheme to a
 * specific subtree of the DOM, rather than relying on the global `prefers-color-scheme` media query.
 *
 * @returns A tuple of:
 * - ref: attach this to the element whose colour scheme should be read
 * - scheme: the colour scheme in effect for that element; `undefined` while no element is attached, or when nothing in
 *   the document declares a scheme at all
 */
export function useLocalColorScheme(): [ref: RefCallback<HTMLElement>, scheme: string | undefined] {
	const [scheme, setScheme] = useState<string | undefined>(undefined);

	const ref = useCallback<RefCallback<HTMLElement>>((node) => {
		if (node === null) {
			setScheme(undefined);
			return undefined;
		}

		const read = () => {
			const colorScheme = getComputedStyle(node).colorScheme;
			setScheme(colorScheme === '' || colorScheme === 'normal' ? undefined : colorScheme);
		};

		read();

		// Any `data-color-scheme` change in the document could land on one of our ancestors, and the attribute filter keeps
		// that to a handful of mutations (roughly one per theme toggle) — cheaper than tracking which elements are ancestors.
		const observer = new MutationObserver(read);
		observer.observe(node.ownerDocument.documentElement, {
			attributes: true,
			subtree: true,
			attributeFilter: ['data-color-scheme'],
		});

		return () => {
			observer.disconnect();
			setScheme(undefined);
		};
	}, []);

	return [ref, scheme];
}
